import { createTheme } from "@mui/material/styles"
import { globalScrollbarStyles } from "../shared/scrollbar"
import { contrastText, densityOf, fontStack, mixHex, normalizeTheme, withAlpha } from "./tokens"

// Turns a token tree (theme/tokens.js) into the MUI theme the whole app runs on.
// Anything a component may need beyond MUI's own slots lives under `theme.holos`.

export function buildAppTheme(input, overrides = {}) {
  const tokens = normalizeTheme(input)
  const { colors, typography, shape, effects } = tokens
  const density = densityOf(tokens)
  const divider = withAlpha(colors.border, shape.borderOpacity)
  const accentSoft = withAlpha(colors.accent, 0.12)
  const ui = fontStack(typography.ui)
  const mono = fontStack(typography.mono)

  return createTheme({
    zIndex: { modal: 10000, snackbar: 11000, tooltip: 12000 },
    spacing: density.spacing,
    palette: {
      mode: tokens.mode,
      primary: { main: colors.accent, contrastText: contrastText(colors.accent) },
      secondary: { main: colors.accentAlt, contrastText: contrastText(colors.accentAlt) },
      background: { default: colors.background, paper: colors.paper },
      text: { primary: colors.text, secondary: colors.textDim },
      divider,
      success: { main: colors.success },
      warning: { main: colors.warning },
      error: { main: colors.error },
      info: { main: colors.info },
      ...(overrides.palette ?? {}),
    },
    typography: {
      fontFamily: ui,
      fontSize: typography.size,
      button: { letterSpacing: `${typography.letterSpacing + 0.04}em` },
      body1: { letterSpacing: `${typography.letterSpacing}em` },
      body2: { letterSpacing: `${typography.letterSpacing}em` },
    },
    shape: { borderRadius: shape.radius },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            ...(effects.glass ? { backdropFilter: `blur(${effects.blur}px)` } : {}),
          },
        },
      },
      MuiPopover: { styleOverrides: { root: { zIndex: 10000 }, paper: { backgroundColor: colors.elevated } } },
      MuiMenu: { defaultProps: { disableScrollLock: true }, styleOverrides: { paper: { backgroundColor: colors.elevated } } },
      MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: colors.elevated, color: colors.text, fontFamily: mono, border: `1px solid ${divider}` } } },
      MuiDialog: { styleOverrides: { paper: { backgroundColor: colors.paper, backgroundImage: "none", border: `1px solid ${divider}` } } },
      MuiButton: { styleOverrides: { root: { fontFamily: mono, borderRadius: shape.radius } } },
      MuiOutlinedInput: { styleOverrides: { root: { fontFamily: mono, borderRadius: shape.radius } } },
      MuiChip: { styleOverrides: { root: { fontFamily: mono, borderRadius: Math.max(2, shape.radius) } } },
      MuiDivider: { styleOverrides: { root: { borderColor: divider } } },
      MuiCssBaseline: {
        styleOverrides: {
          ...globalScrollbarStyles,
          body: {
            backgroundColor: colors.background,
            color: colors.text,
            fontFamily: ui,
            fontSize: `${typography.size}px`,
          },
          '[data-reduce-animations="true"] *, [data-reduce-animations="true"] *::before, [data-reduce-animations="true"] *::after': {
            animationDuration: "0.01ms !important",
            animationIterationCount: "1 !important",
            transitionDuration: "0.01ms !important",
            scrollBehavior: "auto !important",
          },
        },
      },
    },
    holos: {
      tokens,
      accentSoft,
      divider,
      mono,
      ui,
      density: density.value,
      glass: effects.glass,
      blur: effects.glass ? effects.blur : 0,
      glow: effects.glow,
      glowShadow: effects.glow > 0 ? `0 0 ${Math.round(10 + effects.glow * 26)}px ${withAlpha(colors.accent, effects.glow * 0.6)}` : "none",
      surface: (elevation = 0) => (elevation <= 0 ? colors.background : elevation === 1 ? colors.paper : colors.elevated),
    },
  })
}

// Agent pages tint the shared theme with the agent's identity color.
export function buildAgentTheme(input, agent) {
  const tokens = normalizeTheme(input)
  const accent = agent === "friday" ? tokens.colors.friday : tokens.colors.jarvis
  const paper = agent === "friday"
    ? mixHex(tokens.colors.paper, tokens.colors.friday, 0.06)
    : tokens.colors.paper
  return buildAppTheme({ ...tokens, colors: { ...tokens.colors, accent, paper } })
}
